// U5 "Reply without dread": the shapes and words the server and the web app share. Pure, no
// runtime dependencies. The avoidance loop (backlog, anxiety, more avoidance) is met with smaller
// steps: a holding reply ("Got this, I'll get back to you by Friday"), tone and length as a diff
// the owner accepts or rejects, a smaller first step on the second snooze, and dictation.

// ---------------------------------------------------------------------------------------------
// Holding reply
// ---------------------------------------------------------------------------------------------

/** The editable_text block that carries the holding reply on a card. */
export const HOLDING_BLOCK_ID = "holding_reply";
/** The card's send action for it (gmail.send_reply on the holding block; arm → confirm). */
export const HOLDING_ACTION_ID = "send_holding";
export const HOLDING_LABEL = "Holding reply";
export const HOLDING_ACTION_LABEL = "Send holding reply";

export interface HoldingDayChoice {
  /** The owner's local date, YYYY-MM-DD. */
  day: string;
  /** "Fri 23" style, for the picker. */
  label: string;
}

export type HoldingStatus = "drafted" | "sent";

export interface HoldingReplyView {
  cardId: string;
  /** The day the owner says they'll reply by (owner's local date, YYYY-MM-DD). */
  day: string;
  /** How the draft names that day ("Friday", "Mon 2 Nov"). */
  phrase: string;
  /** The next few business days to choose from (the chosen one included). */
  choices: HoldingDayChoice[];
  status: HoldingStatus;
  /** Who wrote the words: one small model call in the owner's voice, or the rules template. */
  by: "model" | "rules";
  /** The task made when it was sent. */
  taskId: string | null;
}

export interface HoldingReplyResult {
  holding: HoldingReplyView;
  /** The card with the holding draft on it (a CardView; typed loosely to stay dependency-free). */
  card: unknown;
}

/** What the thread shows after a holding reply was sent (no day: its text named none). */
export function holdingLine(phrase: string | null): string {
  return phrase ? `You said you'd reply by ${phrase}` : "You sent a holding reply";
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Whether `text` names the day in these words ("Friday", "Mon 2 Nov"), as whole words. */
export function namesHoldingDay(text: string, phrase: string): boolean {
  return !!phrase.trim() && new RegExp(`\\b${escapeRe(phrase.trim())}\\b`, "i").test(text);
}

// ---------------------------------------------------------------------------------------------
// Tone and length
// ---------------------------------------------------------------------------------------------

export const TONES = ["shorter", "warmer", "firmer", "formal"] as const;
export type Tone = (typeof TONES)[number];

export const TONE_LABELS: Readonly<Record<Tone, string>> = {
  shorter: "Shorter",
  warmer: "Warmer",
  firmer: "Firmer",
  formal: "More formal",
};

export function isTone(value: unknown): value is Tone {
  return typeof value === "string" && (TONES as readonly string[]).includes(value);
}

export interface RewriteResult {
  text: string;
  by: "model" | "rules";
  /** False when the rewrite came back the same as the text sent. */
  changed: boolean;
}

// ---------------------------------------------------------------------------------------------
// The second snooze
// ---------------------------------------------------------------------------------------------

export const SMALLER_STEP_TITLE = "Want a smaller first step?";
export const LET_GO_COPY = "Letting go is a decision too.";
/** The reason recorded when the owner lets a card go from the smaller-step offer. */
export const LET_GO_REASON = "Let it go";

export const SMALLER_STEP_CHOICES = [
  "holding_reply",
  "task",
  "make_time",
  "let_go",
  "snooze",
] as const;
export type SmallerStepChoice = (typeof SMALLER_STEP_CHOICES)[number];

// ---------------------------------------------------------------------------------------------
// Dictation (experiment, Chrome only, off by default)
// ---------------------------------------------------------------------------------------------

export interface DictationSetting {
  on: boolean;
}

/** The owner's draft instruction a dictated reply becomes. */
export function dictationInstruction(transcript: string): string {
  return `turn this into a reply in my voice: ${transcript.replace(/\s+/g, " ").trim()}`;
}
